import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download, Loader2 } from 'lucide-react';

const DownloadOptionsDialog = ({ open, onOpenChange, documentType, documentId, documentName }) => {
  const [ribs, setRibs] = useState([]);
  const [selectedRibId, setSelectedRibId] = useState('none');
  const [loading, setLoading] = useState(false);
  const [loadingRibs, setLoadingRibs] = useState(true);

  useEffect(() => {
    if (open) {
      loadRibs();
    }
  }, [open]);

  const loadRibs = async () => {
    setLoadingRibs(true);
    try {
      const res = await axios.get(`${API}/ribs`, { withCredentials: true });
      setRibs(res.data);
      
      // Select default RIB if available
      const defaultRib = res.data.find(r => r.is_default);
      if (defaultRib) {
        setSelectedRibId(defaultRib.id);
      }
    } catch (error) {
      console.error('Error loading RIBs:', error);
    } finally {
      setLoadingRibs(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      // Construct the URL with RIB parameter
      const ribParam = selectedRibId === 'none' ? 'none' : selectedRibId;
      const url = `${API}/${documentType}/${documentId}/pdf?rib_id=${ribParam}`;
      
      // Trigger download
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = documentName || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      // Close dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Options de Téléchargement</DialogTitle>
          <DialogDescription>
            Choisissez les informations bancaires à inclure dans le document PDF.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {loadingRibs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rib-select">RIB à inclure</Label>
                <Select value={selectedRibId} onValueChange={setSelectedRibId}>
                  <SelectTrigger id="rib-select">
                    <SelectValue placeholder="Sélectionner un RIB" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      ❌ Ne pas inclure de RIB
                    </SelectItem>
                    {ribs.map((rib) => (
                      <SelectItem key={rib.id} value={rib.id}>
                        {rib.label} {rib.is_default ? '(Par défaut)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">
                  Le RIB sera affiché dans le pied de page du document.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDownload}
            disabled={loading || loadingRibs}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Téléchargement...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Télécharger
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadOptionsDialog;
